import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import '../../assets/styles/infrastructure_leasing_modal.scss';
import {
  dispatchInfrastructureNotificationsRefresh,
  postBuyerInfrastructureSellerReview,
} from '../../services/infrastructureLeasingListing.service';

function StarRow({ label, value, onChange }) {
  return (
    <div className="mb-3">
      <div className="ile-form-label">
        <span>{label}</span>
      </div>
      <div className="d-flex flex-wrap gap-1" role="group" aria-label={label}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            className={`btn btn-sm ${value === n ? 'btn-warning text-dark' : 'btn-outline-secondary'}`}
            onClick={() => onChange(n)}
            aria-pressed={value === n}
            aria-label={`${n} stars`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Buyer: rate seller / listing experience after payment (one submission per enquiry).
 */
export default function InfrastructureLeasingSellerReviewModal({
  open,
  onClose,
  listingId,
  enquiryId,
  listingTitle,
  assetLabel,
  onSuccess,
}) {
  const [title, setTitle] = useState('');
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const suffix = `${listingId || 'x'}-${enquiryId || 'y'}`;

  useEffect(() => {
    if (open) {
      setTitle('');
      setRating(5);
      setComment('');
      setSubmitting(false);
    }
  }, [open, listingId, enquiryId]);

  if (!open) return null;

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const lid = Number(listingId);
    const eid = Number(enquiryId);
    if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
      toast.error('Missing listing or enquiry.');
      return;
    }
    const titleTrim = title.trim();
    if (titleTrim.length < 2) {
      toast.warning('Enter a review title (at least 2 characters).');
      return;
    }
    if (!rating || rating < 1 || rating > 5) {
      toast.warning('Choose an overall rating from 1 to 5.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await postBuyerInfrastructureSellerReview(lid, eid, {
        title: titleTrim.slice(0, 512),
        rating,
        comment: comment.trim() || undefined,
      });
      if (res?.success) {
        toast.success(res.message || 'Thank you — your review was submitted.');
        dispatchInfrastructureNotificationsRefresh();
        onSuccess?.(res.data);
        onClose();
      } else {
        toast.error(res?.message || 'Could not submit review.');
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message;
      toast.error(msg || 'Could not submit review.');
    } finally {
      setSubmitting(false);
    }
  };

  const modalUi = (
    <div className="ile-modal-root">
      <div className="ile-modal-overlay" onClick={() => !submitting && onClose()} role="presentation">
        <div
          className="ile-modal-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`ile-seller-review-title-${suffix}`}
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="ile-modal-panel__header">
            <button
              type="button"
              className="ile-modal-panel__close"
              onClick={() => !submitting && onClose()}
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
            <h2 className="ile-modal-panel__title" id={`ile-seller-review-title-${suffix}`}>
              Submit review &amp; rating
            </h2>
            <p className="ile-modal-panel__subtitle">
              {listingTitle || 'Listing'} · {assetLabel || `Enquiry #${enquiryId}`}
            </p>
          </div>

          <form className="ile-modal-panel__form" onSubmit={handleSubmit} noValidate>
            <div className="ile-modal-panel__body">
              <div className="ile-modal-block">
                <p className="small text-muted mb-3">
                  Share feedback about this seller and your experience. You can submit one review per completed payment.
                </p>
                <div className="mb-3">
                  <label className="ile-form-label" htmlFor={`ile-rev-title-${suffix}`}>
                    Review title <span className="text-danger">*</span>
                  </label>
                  <input
                    id={`ile-rev-title-${suffix}`}
                    type="text"
                    className="input-pill"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Short headline for your review"
                    maxLength={512}
                    autoComplete="off"
                    required
                  />
                </div>
                <StarRow label="Overall rating *" value={rating} onChange={setRating} />
                <div className="mb-3">
                  <label className="ile-form-label" htmlFor={`ile-rev-comment-${suffix}`}>
                    Review comment (optional)
                  </label>
                  <textarea
                    id={`ile-rev-comment-${suffix}`}
                    className="input-pill"
                    style={{ minHeight: 100, resize: 'vertical' }}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="What went well? What could improve?"
                    maxLength={4000}
                  />
                </div>
              </div>
            </div>

            <div className="ile-modal-panel__footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="ile-modal-btn-primary" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit review'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modalUi, document.body);
  }
  return modalUi;
}
